import { isEmpty, isSchoolAdmin, isSuperAdmin, getBooleanFilterDefaults, buildTopCountPartitionDomainList, DIRECTORY_TOP_COUNT_FILTER_KEYS, TOP_COLLEGE_GROUP_LABEL, TOP_COMPANY_GROUP_LABEL, mapDomainElementsInCodeOrder, appendCustomCompanyFilterOptions, getDirectoryFilterCountsKey } from '../../../library/Core/SmartFunctions';
import { DomainElement, State } from '../../../library/Core/SmartTypes';
import { axiosClient } from '../../../library/Service/axiosClient';
import PageConfigService from '../../../services/page-config.service';
import { getDirectoryFiltersCache, setDirectoryFiltersCache } from './directoryFiltersCache';

class AlumniDirectoryService {
    static pageLoader(params: any) {
        const state: any = {};
        const { pageName, selectedCard, searchBy, jobCompanyId, universityId, pgUniversityId, industryCode, functionalAreaCode, seniorityLevelCode, instituteId } = params;

        // Build default search criteria early — both helpers only read from localStorage
        // so they work before the component state exists. Firing the search in parallel
        // with the other page-config calls shaves the full search round-trip off the
        // perceived load time (it used to start only after all 5 loaders finished).
        const minimalState = { data: { searchLevel: 'MY_SCHOOL' } } as any;
        const defaultCriteria = AlumniDirectoryService.getDefaultSearchCriteria(searchBy);
        const cardCriteria = AlumniDirectoryService.getCriteriaForSelectedCard('totalJoinedAlumni', minimalState);
        const initialSearchCriteria = {
            ...defaultCriteria,
            ...(cardCriteria || {}),
            // ?jobCompanyId=<id> / ?universityId=<id> / ?pgUniversityId=<id> on the route
            // (see alumni-routes.jsx) pre-filter the preloaded search itself, not just the UI checkbox.
            ...(jobCompanyId ? { jobCompanyId } : {}),
            ...(universityId ? { universityId } : {}),
            ...(pgUniversityId ? { pgUniversityId } : {}),
            ...(industryCode ? { industryCode } : {}),
            ...(functionalAreaCode ? { functionalAreaCode } : {}),
            ...(seniorityLevelCode ? { seniorityLevelCode } : {}),
            ...(instituteId ? { instituteId, schoolId: instituteId } : {}),
            context: { ...defaultCriteria.context, page: 1 },
        };

        return Promise.all([
            PageConfigService.get(pageName),
            AlumniDirectoryService.getUserSearchFilterDomainsRaw(),
            AlumniDirectoryService.getSummaryCounts('MY_SCHOOL', searchBy),
            AlumniDirectoryService.getFilterDomain(),
            AlumniDirectoryService.getTopAlumni(),
            AlumniDirectoryService.get(initialSearchCriteria).catch(() => ({ data: { students: [] } })),
        ]).then((values) => {
            state.formConfig = values[0].data;

            const searchPayload = values[5]?.data;
            state.data = {
                searchLevel: 'MY_SCHOOL',
                searchBy,
                students: searchPayload?.students || [],
                pagination: searchPayload?.pagination || { page: 1, pageSize: 50, total: 0, hasMore: false },
                preloadedSearch: true,   // tells DirectorySearchControl to skip the duplicate initial search
                selectedCard: selectedCard ?? 'totalJoinedAlumni',
                summary: values[2].data,
                summaryCountWithoutFilter: values[2].data,
            };

            state.internal = {
                searchFilterDomains: isSchoolAdmin() ? values[3].data : values[1].data,
            };

            // console.log('values 123');
            // console.log(values[4]);
            // return ;
            state.internal.getTopAlumni = values[4];

            return Promise.resolve(state);
        });
    }

    static async get(params: any) {
        return await axiosClient().post(`${process.env.REACT_APP_REPORTS_SERVER}/alumni-directory/search-v2`, params);
    }

    // search-filter-domains backs state.internal.searchFilterDomains for regular
    // user (student/alumni) logins only — school-admins get theirs from
    // getFilterDomain() instead (see pageLoader). Skip the call for school-admins
    // since its result is discarded either way.
    static async getUserSearchFilterDomainsRaw() {
        if (isSchoolAdmin()) return Promise.resolve({ data: {} });
        return await axiosClient().get(`${process.env.REACT_APP_REPORTS_SERVER}/alumni-directory/search-filter-domains`);
    }

    static async getFilterDomain() {
        if (!isSchoolAdmin()) return Promise.resolve({ data: {} });

        // Prefer the login-time cache (prefetchDirectoryFilters) to avoid the slow
        // institute-filters call on every directory open.
        const cached = getDirectoryFiltersCache();
        if (cached) return { data: cached };

        // Cache miss → live call, then self-heal the cache for next time.
        const res = await axiosClient().get(`${process.env.REACT_APP_STUDENT_MANAGEMENT_SERVER}/directory/institute-filters`);
        setDirectoryFiltersCache(res?.data ?? {});
        return res;
    }

    // @Cr By : Irfan @Dt :05 June 2024
    static async getTopAlumni() {
        const user = JSON.parse(localStorage.getItem('user') as string);
        if (isSchoolAdmin() || isSuperAdmin() || user?.userTypeCode !== 'STUDENT') return [];
        console.log("user",user)
        // `studentId` isn't set on localStorage.user by every login flow (e.g. the
        // manual-login + institute-selectio
        // n path never adds it) — `id` is the one
        // field every flow reliably writes, so fall back to it.
        const studentId = user?.studentId ?? user?.id;
        const instituteGroupId=user?.instituteGroupId //By @VP on 26-11-2024 #1176
        if (!studentId || !instituteGroupId) return [];
        // return await axiosClient().get(`${process.env.REACT_APP_REPORTS_SERVER}/alumni-directory/get-top-alumni/${studentId}`);
        const response = await axiosClient().get(`${process.env.REACT_APP_REPORTS_SERVER}/common/alumni-top-match/${studentId}/${instituteGroupId}`);
         return response.data.fetchedAlumni;
    }

    static async performSearchWithUserSelectedCriteria(searchCriteria: any, state: State, dispatch: any, newState: any = {}) {
        const { searchLevel, searchBy } = state.data;
        const defaultCriteria = AlumniDirectoryService.getDefaultSearchCriteria(searchBy);
        const levelFromSearchCriteria = searchCriteria?.context?.level;

        const criteria = {
            ...defaultCriteria,
            ...searchCriteria,
            context: { ...defaultCriteria.context, ...searchCriteria?.context, level: levelFromSearchCriteria ?? searchLevel, page: 1 },
        };

        const requestURL = `${process.env.REACT_APP_REPORTS_SERVER}/alumni-directory/search-v2`;
        const response = await axiosClient().post(requestURL, criteria);

        const summaryResponse = await this.getSummaryCountsBasedOnCampusFilter('MY_SCHOOL', searchCriteria, dispatch);
        dispatch({
            type: 'REFRESH_DATA',
            payload: {
                data: {
                    ...state.data,
                    selectedCard: searchCriteria?.context?.selectedCard ?? state.data.selectedCard,
                    students: response.data.students,
                    pagination: response.data.pagination,
                    filters: response.data.filters,
                    ...newState,
                    summary: summaryResponse?.data || state.data.summaryCountWithoutFilter,
                },
            },
        });
    }

    // Fetch the next page and APPEND results to the existing student list.
    // Called by AlumniCardGridControl on InfiniteScroll trigger.
    static async fetchNextPage(state: State, dispatch: any) {
        const pagination = (state.data as any).pagination;
        if (!pagination?.hasMore) return;

        const nextPage = (pagination.page ?? 1) + 1;
        const criteria = {
            ...(state.data as any).filters,
            context: { ...(state.data as any).filters?.context, page: nextPage },
        };

        const requestURL = `${process.env.REACT_APP_REPORTS_SERVER}/alumni-directory/search-v2`;
        const response = await axiosClient().post(requestURL, criteria);

        const newStudents = response.data.students ?? [];
        // Backend only returns total on page 1; carry it forward from existing pagination.
        const existingPagination = (state.data as any).pagination ?? {};
        const existingTotal = existingPagination.total ?? null;
        const newPagination = {
            ...(response.data.pagination ?? {}),
            total: response.data.pagination?.total ?? existingTotal,
            multipleCurrentRolesCount:
                response.data.pagination?.multipleCurrentRolesCount ?? existingPagination.multipleCurrentRolesCount ?? null,
        };
        dispatch({
            type: 'REFRESH_DATA',
            payload: {
                data: {
                    ...state.data,
                    // If the server returned 0 rows (total is a multiple of PAGE_SIZE),
                    // keep the existing list and set hasMore=false so InfiniteScroll stops.
                    students: newStudents.length > 0
                        ? [...((state.data as any).students || []), ...newStudents]
                        : (state.data as any).students || [],
                    pagination: newStudents.length > 0
                        ? newPagination
                        : { ...newPagination, hasMore: false },
                },
            },
        });
    }

    static async getSummaryCountsBasedOnCampusFilter(level: string = 'MY_SCHOOL', searchFilters: any, dispatch: any) {
        console.log(searchFilters?.instituteId);
        const user = JSON.parse(localStorage.getItem('user') as string);
        const { instituteId: schoolId, instituteGroupId: schoolGroupId, passOutYear } = user;

        const skillToTarget = AlumniDirectoryService.getSkillToTarget();
        const schoolCondition = level === 'MY_SCHOOL' ? { schoolId: [schoolId] } : { schoolGroupId: [schoolGroupId], schoolId: [] };
        const searchCriteria = { skillToTarget, passOutYear, level, ...schoolCondition, requestedFrom: 'directory' };

        if (!isEmpty(searchFilters?.instituteId)) {
            searchCriteria['schoolId'] = searchFilters?.instituteId;
            const summaryCount = await axiosClient().post(
                `${process.env.REACT_APP_REPORTS_SERVER}/alumni-directory/student-summary-counts`,
                searchCriteria
            );

            return summaryCount;
        }
    }

    static getCriteriaForSelectedCard = (selectedCard: string, state: State) => {
        // const user = JSON.parse(sessionStorage.getItem('user') as string);
        const user = JSON.parse(localStorage.getItem('user') as string);
        const { instituteGroupId: schoolGroupId, passOutYear } = user;
        const schoolId = (isSchoolAdmin())  ? user.institutes : [user.instituteId]
        const directoryLevel = state.data.searchLevel;

        const schoolCondition = directoryLevel === 'MY_SCHOOL' ? { schoolId: schoolId } : { schoolGroupId: [schoolGroupId] };

        const criteriaLookup: any = {
            totalJoinedAlumni: { ...schoolCondition, ugCollegeExists: true },
            activeAlumni: { ...schoolCondition, userStatusCode: 'ACTIVE' },
            pursuingHigherStudies: { ...schoolCondition, isPursuingHigherStudies: 1 },
            fromPrestigiousCollege: { ...schoolCondition, isPrestigiousCollege: 1 },
            studyingAbroad: { ...schoolCondition, isStudyingAbroad: 1 },
            settledAbroad: { ...schoolCondition, isSettledAbroad: 1 },
            inTopCompany: { ...schoolCondition, isInTopCompany: 1 },
            entrepreneurs: { ...schoolCondition, isEntrepreneur: 1 },
            inLeadershipRoles: { ...schoolCondition, isInLeadershipRoles: 1 },
            workingAlumni: { ...schoolCondition, isWorking: 1 },
            fromYourBatch: { ...schoolCondition, passOutYear: [passOutYear] },
            sameNextLevelInterests: {
                ...schoolCondition,
                skillToTarget: AlumniDirectoryService.getSkillToTarget(),
                userStatusCode: 'ACTIVE',
            },
        };

        return criteriaLookup[selectedCard];
    };

    static async getSummaryCounts(level: string = 'MY_SCHOOL', searchBy: string = '') {
        // const user = JSON.parse(sessionStorage.getItem('user') as string);
        const user = JSON.parse(localStorage.getItem('user') as string);
        const { instituteId: schoolId, instituteGroupId: schoolGroupId, passOutYear } = user;
        // const schoolId = sessionStorage.getItem('user-school-id');
        // const schoolGroupId = sessionStorage.getItem('user-school-group-id');
        // const passOutYear: any = sessionStorage.getItem('pass-out-year');

        const skillToTarget = AlumniDirectoryService.getSkillToTarget();
        const schoolCondition = level === 'MY_SCHOOL' ? { schoolId: [schoolId] } : { schoolGroupId: [schoolGroupId], schoolId: [] };
        const searchCriteria = { skillToTarget, passOutYear, level, ...schoolCondition };

        const summaryCount = await axiosClient().post(
            `${process.env.REACT_APP_REPORTS_SERVER}/alumni-directory/student-summary-counts`,
            searchCriteria
        );

        return summaryCount;
    }

    static getDefaultSearchCriteria(searchBy: string = '') {
        // const passOutYear = parseInt(sessionStorage.getItem('pass-out-year') as string);
        // const user = JSON.parse(sessionStorage.getItem('user') as string);
        const user = JSON.parse(localStorage.getItem('user') as string);
        const { passOutYear} = user;
        const instituteId = (isSchoolAdmin())  ? user.institutes : user.instituteId
        return {
            context: { searchBy, passOutYear, level: 'MY_SCHOOL' },
            passOutYear: [],
            educationOrEmploymentCurrentStatusCode: [],
            fieldOfStudyCode: [],
            universityId: [],
            pgUniversityId: [],
            curriculumCode: [],
            residingCountryCode: [],
            residingCityCode: [],
            collegeCountryCode: [],
            pgCollegeCountryCode:[],
            collegeCityCode: [],
            instituteId: [],
            entranceTestCode: [],
            skills: [],
            interests: [],
            programId: [],
            jobCompanyId: [],
            functionalAreaCode: [],
            industryCode: [],
            workingStatusCode: [],
            careerTrack: [],
            // userStatusCode: 'ACTIVE',
            schoolId: instituteId,
            higherStudyAtUG : null,
            seniorityLevelCode:[],
            typeOfPracticeCode:[],
            jobRoleCode:[],
            defenceServiceBranchCode:[],
            ...getBooleanFilterDefaults(AlumniDirectoryService.filterConfig),
        };
    }

    static async getCustomDomains() {
        // const user = JSON.parse(sessionStorage.getItem('user') as string);
        const user = JSON.parse(localStorage.getItem('user') as string);
        const { instituteId: schoolId, instituteGroupId: schoolGroupId } = user;
        // const schoolId = sessionStorage.getItem('user-school-id');
        // const schoolGroupId = sessionStorage.getItem('user-school-group-id');

        return await axiosClient().get(
            `${process.env.REACT_APP_REPORTS_SERVER}/alumni-directory/search-filter-domains?schoolId=${schoolId}&schoolGroupId=${schoolGroupId}`
        );
    }

    static getSearchFilterDomains(domain: any, customDomains: any) {
        // const user = JSON.parse(sessionStorage.getItem('user') as string);
        const user = JSON.parse(localStorage.getItem('user') as string);
        const { passOutYear } = user;

        const getDomainForCodes = (domain: any, categoryCode: string, codes: any) => {
            if (isEmpty(codes) || !Array.isArray(codes)) return [];
            const stringCodes = codes?.map((code: any) => code?.toString());
            let targetDomain = domain?.get(categoryCode)?.filter((domainValue: any) => stringCodes.includes(domainValue.code));
            if (categoryCode === 'PAST_YEAR_CODE') {
                targetDomain = targetDomain.map((domain: any) =>
                    domain.code == passOutYear ? { ...domain, value: `${domain.value} (your batch)` } : domain
                );
                return targetDomain?.sort((a: any, b: any) => b.value.toLowerCase().localeCompare(a.value.toLowerCase()));
            } else if (categoryCode === 'COUNTRY_CODE') {
                return targetDomain;
            } else {
                return targetDomain?.sort((a: any, b: any) => a.value.toLowerCase().localeCompare(b.value.toLowerCase()));
            }
        };

        const searchFilterDomains = {};

        customDomains &&
            Object.keys(customDomains).forEach((key: any) => {
                if (!AlumniDirectoryService.filterConfig[key]) return;

                const categoryCode = AlumniDirectoryService.filterConfig[key]?.domain;
                let items: DomainElement[];
                if (DIRECTORY_TOP_COUNT_FILTER_KEYS.has(key)) {
                    const counts = customDomains[getDirectoryFilterCountsKey(key)] ?? null;
                    items = mapDomainElementsInCodeOrder(domain, categoryCode, customDomains[key]);
                    if (key === 'jobCompanyId') items = appendCustomCompanyFilterOptions(items, customDomains[key]);
                    items = buildTopCountPartitionDomainList(items, customDomains[key], 10, counts);
                } else {
                    items = getDomainForCodes(domain, categoryCode, customDomains[key]);
                }
                Object.assign(searchFilterDomains, { [key]: items });
            });

        return searchFilterDomains;
    }

    static getDomainForCodes(domain: any, categoryCode: string, codes: any) {
        return domain.get(categoryCode)?.values.filter((domainValue: any) => codes.includes(domainValue.code));
    }

    static getSummaryCardControlImage = (controlName: string) => {
        const controlImageMap: any = {
            totalJoinedAlumni: '',
            pursuingHigherStudies: '',
            workingAlumni: '',
            fromYourBatch: '',
            sameNextLevelInterests: '',
        };
    };

    static getSkills = () => JSON.parse(localStorage.getItem('user') as string)?.skills?.split(',');
    static getSkillToTarget = () => JSON.parse(localStorage.getItem('user') as string)?.skillToTarget?.split(',');

    static filterConfig: any = {
        instituteId: {
            id: 'instituteId',
            label: 'Campus',
            domain: 'INSTITUTE_DOMAIN',
            applicableFor: 'BOTH',
            class: 'fiterItem w-20 mb-3',
            isAdditional: false,
            isVisible: true,
        },
        programId: {
            id: 'programId',
            label: 'Program Name',
            domain: 'ALL_PROGRAM_CODE',
            applicableFor: 'BOTH',
            class: 'fiterItem w-20 mb-3',
            isAdditional: false,
            isVisible: true,
        },
        passOutYear: {
            id: 'passOutYear',
            label: 'Batch Year',
            domain: 'PAST_YEAR_CODE',
            directoryLevel: 'BOTH',
            applicableFor: 'BOTH',
            class: 'fiterItem w-20 mb-3',
            isAdditional: false,
            isVisible: true,
            hasSearch: true,
        },
        residingCountryCode: {
            id: 'residingCountryCode',
            label: 'Current Country',
            domain: 'COUNTRY_CODE',
            applicableFor: 'BOTH',
            class: 'fiterItem w-20 mb-3',
            isAdditional: false,
            isVisible: true,
        },
        workingStatusCode: {
            id: 'workingStatusCode',
            label: 'Working Status',
            domain: 'WORK_STATUS_CODE',
            applicableFor: 'BOTH',
            class: 'fiterItem w-20 mb-3',
            isAdditional: false,
            isVisible: true,
        },
        collegeCountryCode: {
            id: 'collegeCountryCode',
            label: 'Country',
            domain: 'COUNTRY_CODE',
            applicableFor: 'BOTH',
            class: 'fiterItem w-20 mb-3',
            isAdditional: false,
            isVisible: false,
        },
        pgCollegeCountryCode: {
            id: 'pgCollegeCountryCode',
            label: 'Country',
            domain: 'COUNTRY_CODE',
            applicableFor: 'BOTH',
            class: 'fiterItem w-20 mb-3',
            isAdditional: false,
            isVisible: false,
        },
        universityId: {
            id: 'universityId',
            label: 'Higher Studies at',
            domain: 'UNIVERSITY_CODE',
            domainGroup: 'topPartition',
            topPartitionGroupLabel: TOP_COLLEGE_GROUP_LABEL,
            directoryLevel: 'BOTH',
            applicableFor: 'BOTH',
            class: 'fiterItem w-40 mb-3',
            isAdditional: false,
            isVisible: true,
        },
        pgUniversityId: {
            id: 'pgUniversityId',
            label: 'Higher Studies at',
            domain: 'UNIVERSITY_CODE',
            domainGroup: 'topPartition',
            topPartitionGroupLabel: TOP_COLLEGE_GROUP_LABEL,
            directoryLevel: 'BOTH',
            applicableFor: 'BOTH',
            class: 'fiterItem w-40 mb-3',
            isAdditional: false,
            isVisible: false,
        },
        fieldOfStudyCode: {
            id: 'fieldOfStudyCode',
            label: 'Course',
            domain: 'FIELD_OF_STUDY_TYPE_CODE',
            applicableFor: 'BOTH',
            class: 'fiterItem w-20 mb-3',
            isAdditional: false,
            isVisible: false,
        },
        jobCompanyId: {
            id: 'jobCompanyId',
            label: 'Company Name',
            domain: 'COMPANY_CODE',
            domainGroup: 'topPartition',
            topPartitionGroupLabel: TOP_COMPANY_GROUP_LABEL,
            applicableFor: 'BOTH',
            class: 'fiterItem w-40 mb-3',
            isAdditional: false,
            isVisible: true,
        },
        seniorityLevelCode: {
            id: 'seniorityLevelCode',
            label: 'Designation Level',
            domain: 'JOB_SENIORITY_LEVEL',
            directoryLevel: 'BOTH',
            applicableFor: 'BOTH',
            class: 'fiterItem w-20 mb-3',
            isAdditional: false,
            isVisible: false,
        },
        functionalAreaCode: {
            id: 'functionalAreaCode',
            label: 'Functional Area',
            domain: 'FUNCTIONAL_AREA_CODE',
            applicableFor: 'BOTH',
            class: 'fiterItem w-40 mb-3',
            isAdditional: true,
            isVisible: true,
        },
        industryCode: {
            id: 'industryCode',
            label: 'Working Industry',
            domain: 'INDUSTRY_CODE',
            applicableFor: 'BOTH',
            class: 'fiterItem w-30 mb-3',
            isAdditional: true,
            isVisible: true,
        },
        entranceTestCode: {
            id: 'entranceTestCode',
            label: 'Exam Appeared',
            domain: 'ENTRANCE_TEST_CODE',
            applicableFor: 'BOTH',
            class: 'fiterItem w-30 mb-3',
            isAdditional: true,
            isVisible: true,
        },
        jobRoleCode: {
            id: 'jobRoleCode',
            label: 'Self-Employed',
            domain: 'FREELANCING_JOB_ROLE_CODE',
            applicableFor: 'BOTH',
            class: 'fiterItem width-25 mb-3',
            isAdditional: true,
            isVisible: true,
        },
        typeOfPracticeCode: {
            id: 'typeOfPracticeCode',
            label: 'Professional Practice',
            domain: 'PROFESSIONAL_PRACTICE_TYPE',
            applicableFor: 'BOTH',
            class: 'fiterItem width-25 mb-3',
            isAdditional: true,
            isVisible: true,
        },
        defenceServiceBranchCode: {
            id: 'defenceServiceBranchCode',
            label: 'Defence',
            domain: 'SERVICE_BRANCH',
            applicableFor: 'BOTH',
            class: 'fiterItem width-25 mb-3',
            isAdditional: true,
            isVisible: true,
        },
        careerTrack: {
            id: 'careerTrack',
            label: 'Career Track',
            domain: 'CAREER_TRACK',
            applicableFor: 'BOTH',
            class: 'fiterItem width-25 mb-3',
            isAdditional: true,
            isVisible: true,
        },
        prestigiousCollege: {
            id: 'prestigiousCollege',
            label: 'Prestigious Colleges',
            filterType: 'boolean',
            isShowProfileFilter: true,
            displayOrder: 1,
            booleanCheckedValue: 1,
            applicableFor: 'BOTH',
            isVisible: false,
        },
        isInTopCompany: {
            id: 'isInTopCompany',
            label: 'Top Companies',
            filterType: 'boolean',
            isShowProfileFilter: true,
            displayOrder: 2,
            booleanCheckedValue: 1,
            applicableFor: 'BOTH',
            isVisible: false,
        },
        isMNC: {
            id: 'isMNC',
            label: 'MNCs',
            filterType: 'boolean',
            isShowProfileFilter: true,
            displayOrder: 3,
            booleanCheckedValue: 1,
            applicableFor: 'BOTH',
            isVisible: false,
        },
        isStartup: {
            id: 'isStartup',
            label: 'Startups',
            filterType: 'boolean',
            isShowProfileFilter: true,
            displayOrder: 4,
            booleanCheckedValue: 1,
            applicableFor: 'BOTH',
            isVisible: false,
        },
        isFortune500: {
            id: 'isFortune500',
            label: 'Fortune500',
            filterType: 'boolean',
            isShowProfileFilter: true,
            displayOrder: 5,
            booleanCheckedValue: 1,
            applicableFor: 'BOTH',
            isVisible: false,
        },
        isUnicorn: {
            id: 'isUnicorn',
            label: 'Unicorns',
            filterType: 'boolean',
            isShowProfileFilter: true,
            displayOrder: 6,
            booleanCheckedValue: 1,
            applicableFor: 'BOTH',
            isVisible: false,
        },
        isNGO: {
            id: 'isNGO',
            label: 'NGOs',
            filterType: 'boolean',
            isShowProfileFilter: true,
            displayOrder: 7,
            booleanCheckedValue: 1,
            applicableFor: 'BOTH',
            isVisible: false,
        },
        isGovernment: {
            id: 'isGovernment',
            label: 'Government',
            filterType: 'boolean',
            isShowProfileFilter: true,
            displayOrder: 8,
            booleanCheckedValue: 1,
            applicableFor: 'BOTH',
            isVisible: false,
        },
        isNotable: {
            id: 'isNotable',
            label: 'Marked as Notable',
            filterType: 'boolean',
            isShowProfileFilter: true,
            displayOrder: 9,
            booleanCheckedValue: 1,
            applicableFor: 'BOTH',
            isVisible: false,
        },
    };
}

export default AlumniDirectoryService;
